<script setup>
import { ref, computed, onMounted } from 'vue'
import { debounce } from '@utils/performance'

/**
 * BaseSearchInput - Reusable search input component with debouncing
 *
 * Emits:
 * - @update:modelValue: v-model binding for keyword value
 * - @search: Debounced search event fired after user stops typing
 * - @clear: Fired when clear button is clicked
 *
 * Slots:
 * - prepend: Content before the input (default: search icon)
 * - append: Content after the input (default: clear icon when hasInputValue is true)
 *   - Slot props: { clear: Function, hasInputValue: Boolean }
 *
 * Appearance is owned here. Pick one with `variant`; callers must not restyle
 * the input or icons from outside.
 */
const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: 'Search',
    },
    debounceDelay: {
        type: Number,
        default: 500,
    },
    autofocus: {
        type: Boolean,
        default: false,
    },
    variant: {
        type: String,
        default: 'header', // header, page, filter
    },
})

const emit = defineEmits(['update:modelValue', 'search', 'clear'])

const input = ref(null)

const localValue = computed({
    get() {
        return props.modelValue
    },
    set(val) {
        emit('update:modelValue', val)
    },
})

const debouncedSearch = debounce(() => {
    emit('search', localValue.value)
}, props.debounceDelay)

function onKeyup() {
    debouncedSearch()
}

function clear() {
    localValue.value = ''
    emit('clear')
}

function focus() {
    input.value && input.value.focus()
}

onMounted(() => {
    if (props.autofocus) {
        focus()
    }
})

defineExpose({ focus })
</script>

<template>
    <div :class="['base-search-input', `base-search-input-${variant}`]">
        <slot name="prepend">
            <BaseIcon
                class="search-icon"
                name="search"
            />
        </slot>
        <input
            ref="input"
            v-model="localValue"
            class="field"
            type="text"
            inputmode="search"
            name="search-query"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            role="searchbox"
            data-lpignore="true"
            data-form-type="other"
            :placeholder="placeholder"
            @keyup.passive="onKeyup"
        >
        <slot
            name="append"
            :clear="clear"
            :has-input-value="!!localValue"
        >
            <BaseIcon
                v-if="localValue"
                class="clear-icon"
                name="x"
                @click="clear"
            />
        </slot>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.base-search-input {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
}

.search-icon,
.clear-icon {
    flex-shrink: 0;
}

.field {
    outline: none;
    border: none;
}

.base-search-input-header {
    .search-icon {
        width: 24px;
        height: 24px;
        margin-right: $s-m;
        fill: $c-white;
    }

    .field {
        flex: 1;
        height: 100%;
        font-size: 16px;
        font-weight: 500;
        color: $c-white;
        background: transparent;

        &::placeholder {
            color: $c-header-searchbar-placeholder;
        }
    }

    .clear-icon {
        width: 20px;
        height: 20px;
        margin-left: $s-m;
        cursor: pointer;
        opacity: 0.7;
        fill: $c-white;
        transition: opacity 0.2s ease;

        &:hover {
            opacity: 1;
        }
    }
}

.base-search-input-page {
    .search-icon {
        width: 24px;
        height: 24px;
        margin-right: $s-m;
        opacity: 0;
        fill: $c-white;

        @include desktop {
            width: 40px;
            height: 40px;
            opacity: 1;
        }
    }

    .field {
        flex: 1;
        min-width: 0;
        font-size: 20px;
        font-weight: bold;
        line-height: 24px;
        color: $c-white;
        text-align: center;
        letter-spacing: -0.83px;
        background: none;

        @include desktop {
            font-size: 40px;
            line-height: 55px;
            text-align: left;
            letter-spacing: -1px;
        }

        &::placeholder {
            color: transparentize($color: $c-white, $amount: 0.2);
        }
    }

    .clear-icon {
        width: 24px;
        height: 24px;
        cursor: pointer;
        fill: $c-white;
    }
}

.base-search-input-filter {
    .search-icon {
        width: 1.5rem;
        height: 1.5rem;
        margin-right: $s-xs;
        fill: $c-french-grey;
    }

    .field {
        flex: 1;
        min-width: 0;
        font-size: 16px; // below 16px iOS zooms the page on focus
        color: $c-french-grey;
        background: none;

        &::placeholder {
            color: $c-french-grey;
        }
    }

    .clear-icon {
        width: 1.5rem;
        height: 1.5rem;
        margin-left: $s-xs;
        cursor: pointer;
        fill: $c-french-grey;
    }
}
</style>
